import React from 'react';

import Avatar from 'ds/Avatar';
import Icon from 'ds/Icon';
import IconButton from 'ds/IconButton';
import Layout from 'ds/Layout';
import Text from 'ds/Text';

import { AddUserResultProps } from './types';

const AddUserResult: React.FC<AddUserResultProps> = ({
  user,
  onDelete,
  requestInProgress,
  badges,
  showDelete,
  isPendingDeletion,
  onUndo,
  showEmailIndicator,
  isNewUser = false,
  sendsEmail = false
}) => {
  const first = user.first || '';
  const last = user.last || '';
  const firstname = user.firstname || first;
  const lastname = user.lastname || last;
  const name = `${firstname} ${lastname}`.trim() || 'Unknown';
  const email = user.email || '';
  const avatarUrl = user.avatar_url;

  const getInitials = () => {
    if (firstname && lastname) {
      return `${firstname[0]}${lastname[0]}`.toUpperCase();
    }
    if (firstname) {
      return firstname[0].toUpperCase();
    }
    if (lastname) {
      return lastname[0].toUpperCase();
    }
    return '?';
  };

  const initials = getInitials();
  const hasAvatar = !!avatarUrl;
  const isNewUserBeingAdded = isNewUser && !isPendingDeletion;

  return (
    <Layout
      paddingX={12}
      paddingY={12}
      direction="row"
      align="center"
      justify="space-between"
      style={{ borderBottom: '1px solid #eceff1' }}
    >
      <Layout direction="row" align="center" flexGrow={1} __style={{ opacity: isPendingDeletion ? 0.2 : undefined }}>
        {hasAvatar ? (
          <Avatar avatarUrl={avatarUrl} size={44} defaultIcon="avatar" />
        ) : isNewUserBeingAdded ? (
          <Layout
            width={44}
            height={44}
            borderRadius={1000}
            color="gray-50"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Icon name="avatar" color="gray-400" size="md" />
          </Layout>
        ) : (
          <Layout
            width={44}
            height={44}
            borderRadius={1000}
            color="blue-600"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Text size="body-xs" semibold color="white">
              {initials}
            </Text>
          </Layout>
        )}
        <Layout marginLeft={8} align="flex-start" flex direction="column" gap={4}>
          <Layout direction="row" align="center" gap={8} style={{ flexWrap: 'wrap' }}>
            <Text
              size="body-sm"
              color="gray-900"
              semibold
              __style={isPendingDeletion ? { textDecoration: 'line-through' } : undefined}
            >
              {name}
            </Text>
            {(user.role || user.title) && (
              <Layout color="gray-50" borderRadius={1000} paddingX={6} paddingY={2} align="center" justify="center">
                <Text size="body-xs" semibold color="gray-900">
                  {user.role || user.title}
                </Text>
              </Layout>
            )}
          </Layout>
          {email && (
            <Text
              size={showDelete ? 'body-sm' : 'body-xs'}
              color="gray-400"
              __style={isPendingDeletion ? { textDecoration: 'line-through' } : undefined}
            >
              {email}
            </Text>
          )}
          {badges && badges.length > 0 && (
            <Layout marginTop={4} direction="row" gap={8}>
              {badges.map((badge, idx) => (
                <Layout
                  key={idx}
                  marginLeft={idx === 0 ? 16 : 0}
                  paddingX={8}
                  paddingY={4}
                  borderRadius={4}
                  borderWidth={1}
                  display="inline"
                >
                  <Text size="overline-sm">{badge.text}</Text>
                </Layout>
              ))}
            </Layout>
          )}
        </Layout>
      </Layout>
      <Layout direction="row" align="center" gap={8}>
        {isNewUserBeingAdded && sendsEmail && !user.id && (
          <Text size="body-sm" color="gray-400" italic>
            Inviting{showEmailIndicator && ' *'}
          </Text>
        )}
        {showDelete &&
          (isPendingDeletion && onUndo ? (
            <Layout align="center">
              <IconButton size="sm" name="plus" onClick={onUndo} type="noBackground" />
            </Layout>
          ) : onDelete ? (
            <Layout align="center">
              <IconButton size="sm" name="close" onClick={onDelete} type="noBackground" disabled={requestInProgress} />
            </Layout>
          ) : null)}
      </Layout>
    </Layout>
  );
};

export default AddUserResult;
